import { getBrowserInfo } from "@/utils/BrowserInfo";
import { Round2String } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { waitForFieldChange } from "@/utils/waitForFieldChange";

export const usePaymentStore = defineStore("paymentStore", () => {
  // Data
  const couponList = ref<any[]>([]);
  const docketId = ref<string>("");
  const docketLineId = ref<string>("");
  const docketPaymentId = ref<string>("");
  const lowBalanceError = ref<boolean>(false);
  const isPendingBooking = ref<boolean>(false);
  const processingPaymentsOverlay = ref<boolean>(false);

  // Methods
  /**
   * Retrieves a list of coupons based on the provided user ID.
   * If no user ID is provided, it retrieves all available coupons.
   *
   * @param {string} userId - The ID of the user for whom to retrieve coupons.
   * @returns {Promise<any[]>} - A promise that resolves to an array of coupon objects.
   */
  const getCouponList = async (userId: any = null) => {
    try {
      couponList.value = await pb.collection("coupons").getFullList({
        sort: "expiryTime",
        filter: userId
          ? `user = "${userId}" && qtyRemaining > 0 && expiryTime >= @now && expired = false`
          : "qtyRemaining > 0 && expiryTime >= @now && expired = false",
      });
    } catch (e) {
      console.error('Erroring getCouponList:', e)
    }
  };

  /**
   * Initiates a docket for a booking and creates or updates the corresponding docket line.
   *
   * @param bookingDetails - Details of the booking.
   * @param bookingId - The ID of the booking.
   *
   * @returns {Promise<void>} - A promise that resolves when the docket and docket line are created or updated.
   */
  const initiateDocketForBooking = async (
    bookingDetails: any,
    bookingId: string,
    source?: string,
    products: any[] = [],
  ) => {
    try {
      console.log('initiateDocketForBooking', bookingDetails)
      let currentDocketId = docketId.value;

      // docket discount for bookings
      const discountRate = bookingDetails?.discountBookings || 0; // percentage discount

      // Get DocketLines with This booking
      let docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + bookingId + '"'})

      // Check if these Products are all the paid docket
      let finalProducts = [];
      if (docketLines && docketLines.length > 0) {
        // Get DocketLines for dockets

        let AlldocketIDs = docketLines.map(dl => 'docket="' + dl.docket +'"').join("||")
        let allDocketLines = await pb.collection("docketLines").getFullList({filter: AlldocketIDs, expand:"docket"})

        if(allDocketLines) {
          for (const product of products) {
            let addProduct = true
            allDocketLines.forEach(dl => {
              if (dl.product == product.id && (dl?.expand?.docket?.status == "Paid" || dl?.expand?.docket?.status == "Closed")) {
                addProduct = false
              }
            })
            if (addProduct) {
              finalProducts.push(product)
            }
          }
          products = finalProducts;
        }
      }

      // Get Booking
      // If paid and due match the booking in the DB
      if(currentDocketId) {
        try {
          const docketStatus = await pb.collection("dockets").getOne(currentDocketId, { fields: "id,status" });
          if (docketStatus && (docketStatus.status === "Paid" || docketStatus.status === "Closed")) {
            return;
          }
          let bookingCheck = await pb.collection("bookings").getOne(bookingId)
          if (bookingCheck && bookingCheck?.paid == bookingDetails?.paid && bookingCheck.due == bookingDetails?.due && currentDocketId) {
            return
          }
        } catch(e){
          console.error("Error fetching booking details:", e);
        }
      }

      // Prepare docket data
      let docketData: any = {
        location: bookingDetails?.location,
        discountProducts: bookingDetails?.discountProduct,
        discountBookings: bookingDetails?.discountBookings,
        discountEvents: bookingDetails?.discountEvents,
        due: bookingDetails?.due,
        printList: false,
        user: bookingDetails?.loginUser || bookingDetails?.owner || "",
        source: source || "Booking",
        discountSources: bookingDetails?.discountSources,
      };

      // Refund flow: If due is negative, it means the customer has overpaid and we need to create a refund docket
      if (bookingDetails?.due < 0) {
        try {
          // Create a new docket
          console.log('create refund docket initiatedocketforbooking')
          const docketResponse = await pb.collection("dockets").create({
            ...docketData,
            createdBy: bookingDetails?.loginUser,
            status: "Paid",
          });

          if (docketResponse?.id) {
            const newDocketId = docketResponse.id;
            const qty = Math.floor(bookingDetails?.qty) || 1;
            const basePrice = bookingDetails.todAdjustment > 0 ? bookingDetails.itemPrice * (1 + bookingDetails.todAdjustment / 100) : bookingDetails.itemPrice;

            // Prepare docket line data
            const docketLineData = {
              docket: newDocketId, // Use the newly created docket ID
              transactionType: "Booking Payment",
              qty: qty,
              itemPrice: bookingDetails?.itemPrice,
              docketDiscount: discountRate,
              todAdjustment: bookingDetails.todAdjustment,
              todSource: bookingDetails.todSource,
              discountApplied: bookingDetails.discountApplied,
              lineDiscountTotal: bookingDetails?.lineDiscountTotal,
              lineTotal: Round2String(qty * basePrice - (bookingDetails?.lineDiscountTotal || 0), 2),
              userAccountCredit: bookingDetails?.owner,
              status: "Available",
              createdBy: bookingDetails?.loginUser,
              booking: bookingId,
            };

            // Create a new docket line
            const docketLineResponse = await pb.collection("docketLines").create(docketLineData);


            if (products.length > 0) {
              // Check if these Products are all the paid docket
              for (const product of products) {
                const productDocketLineData = {
                  docket: newDocketId,
                  transactionType: "Product",
                  product: product.id,
                  qty: product.qty || 1,
                  docketDiscount: product.docketDiscount,
                  todAdjustment: product.todAdjustment,
                  lineDiscountTotal: product.lineDiscountTotal,
                  discountApplied: product.discountApplied,
                  itemPrice: product.sellPrice,
                  lineTotal: (product.qty || 1) * product.itemPrice,
                  status: "Available",
                  createdBy: bookingDetails?.loginUser,
                };

                // Create a new docket line for each product
                const productDocketLineResponse = await pb.collection("docketLines").create(productDocketLineData);

              }
            }

            if (docketLineResponse?.id) {
              docketLineId.value = docketLineResponse.id;

              // Update the booking with the new docket line ID
              await pb.collection("bookings").update(bookingId, {
                docketLine: docketLineResponse.id,
              });
            }
          }

          return;
        } catch (error) {
          console.error("Error initiating docket for booking:", error);
          // Don't fall through to the non-refund flow below and create a second,
          // unintended docket for this booking - surface the failure instead.
          throw error;
        }
      }

      // Create or update the docket
      // Add addiontal docket data for non refund flow
      docketData.discCodeBookings = bookingDetails?.discCodeBookings;
      docketData.discCodeProduct = bookingDetails?.discCodeProduct;
      docketData.discMemBookings = bookingDetails?.discMemBookings;
      docketData.discMemProducts = bookingDetails?.discMemProducts;

      if (!currentDocketId) {
        console.log('create docket')
        const docketResponse = await pb.collection("dockets").create({
          ...docketData,
          createdBy: bookingDetails?.loginUser,
          foodRequiredTime: bookingDetails?.startTime,
          status: "Open",
        });

        if (docketResponse) {
          currentDocketId = docketResponse.id;
          docketId.value = currentDocketId;
        }
      } else {
        await pb.collection("dockets").update(currentDocketId, {
          ...docketData,
          updatedBy: bookingDetails?.loginUser,
        });
      }

      // Create or update the docket line
      if (currentDocketId) {
        console.log('create docket line', bookingDetails)
        const qty = Math.floor(bookingDetails?.qty) || 1;
        const basePrice = bookingDetails.todAdjustment > 0 ? bookingDetails.itemPrice * (1 + bookingDetails.todAdjustment / 100) : bookingDetails.itemPrice;
        // Check current Docket Line
        const docketLineData = {
          docket: currentDocketId,
          transactionType: "Booking Payment",
          qty: qty,
          itemPrice: bookingDetails?.itemPrice,
          docketDiscount: discountRate, // Apply booking discount rate to the docket line
          todAdjustment: bookingDetails.todAdjustment,
          todSource: bookingDetails.todSource,
          discountApplied: bookingDetails.discountApplied,
          lineDiscountTotal: bookingDetails?.lineDiscountTotal,
          lineTotal: Round2String(qty * basePrice - (bookingDetails?.lineDiscountTotal || 0), 2),
          status: "Available",
          createdBy: bookingDetails?.loginUser,
          booking: bookingId,
        };

        try {
          // Use the docket id captured for this call, not the shared store ref,
          // which another in-flight booking/payment can reassign before we get here.
          const existingProductLines = await pb
            .collection("docketLines")
            .getFullList({
              filter: `docket = "${currentDocketId}" && transactionType = "Product"`,
            });

          for (const line of existingProductLines) {
            await pb.collection("docketLines").delete(line.id);
          }
        } catch (error) {
          console.error("Error deleting existing product docket lines:", error);
        }

        if (products.length > 0) {
          for (const product of products) {
            console.log("product", product)
            const productDocketLineData = {
              docket: currentDocketId,
              transactionType: "Product",
              product: product.id,
              qty: product.qty || 1,
              docketDiscount: product.docketDiscount,
              todAdjustment: product.todAdjustment,
              todSource: product.todSource,
              lineDiscountTotal: product.lineDiscountTotal,
              discountApplied: product.discountApplied,
              itemPrice: product.sellPrice,
              lineTotal: (product.qty || 1) * product.itemPrice,
              status: "Available",
              createdBy: bookingDetails?.loginUser,
              booking: bookingId,
            };

            // Create a new docket line for each product
            const productDocketLineResponse = await pb.collection("docketLines").create(productDocketLineData);

          }
        }

        // docketLineId is a shared store ref that another in-flight booking/payment can
        // reassign before we get here, so verify it still belongs to this booking before
        // reusing it - otherwise we'd silently move an unrelated line onto this docket.
        let existingLineForBooking = docketLineId.value
          ? await pb.collection("docketLines").getOne(docketLineId.value).catch(() => null)
          : null;
        if (existingLineForBooking && existingLineForBooking.booking !== bookingId) {
          console.error(
            "docketLineId mismatch: expected booking " + bookingId + " but found " + existingLineForBooking.booking + ". Ignoring stale docketLineId."
          );
          existingLineForBooking = null;
        }

        if (existingLineForBooking) {
          // Update the existing docket line
          if (existingLineForBooking.itemPrice == 0) {
            docketLineData.itemPrice = 0;
          }

          await pb.collection("docketLines").update(existingLineForBooking.id, docketLineData);
        } else {
          // Create a new docket line
          const docketLineResponse = await pb.collection("docketLines").create(docketLineData);

          if (docketLineResponse) {
            docketLineId.value = docketLineResponse.id;

            await pb.collection("bookings").update(bookingId, {
              docketLine: docketLineResponse.id,
            });
          }
        }
      }
    } catch (e) {
      console.error("Error initiating docket for booking:", e);
      // Callers must not proceed to charge a coupon/gift voucher/card against a
      // docket that failed to be created or updated - let this fail loudly.
      throw e;
    }
  };

  /**
   * This function handles the payment process for gift vouchers or coupons.
   *
   * @param params - An object containing the payment details and other parameters.
   * @param params.status - The status of the payment.
   * @param params.paymentType - The type of payment (either "Coupon" or "Gift Voucher").
   * @param params.amount - The amount of the payment.
   * @param params.identifier - The identifier of the coupon or gift voucher.
   * @param params.qty - The quantity of the coupon.
   * @param params.toPay - The total amount to pay.
   * @param params.type - The type of payment (e.g., "Booking").
   * @param params.bookingId - The ID of the booking.
   * @param params.totalPaid - The total amount paid for the booking.
   * @param params.userId - The ID of the user making the payment.
   * @param params.payMethods - The payment methods used for the booking.
   *
   * @returns A promise that resolves to the updated coupon or gift voucher record.
   */
  const payWithGiftVoucherOrCoupon = async (params: {
    status?: string;
    paymentType: "Coupon" | "Gift Voucher";
    amount: number;
    identifier: string;
    qty: number;
    toPay: number;
    type?: string;
    bookingId?: string;
    totalPaid?: number;
    userId: string;
    payMethods?: string[];
    source?: string;
    giftValue?: any;
  }) => {
    try {
      const createPaymentRecord = async (data: any) => {
        const response = await pb.collection("dockets").update(docketId.value, {
          user: params?.userId ? data?.owner : "",
          updatedBy: params?.userId,
        });

        const record = await pb.collection("docketPayments").create(data);
        docketPaymentId.value = record.id;
        const balanceRemaining = params?.toPay - params?.amount;
        const due = balanceRemaining <= 0 ? 0 : Number(balanceRemaining).toFixed(2);

        if ((params?.type === "Booking" || params?.type === "Guest Booking" || params?.type === "Quick User Booking") && params.bookingId) {
          // update due of booking record

          let bookingCheck = await pb.collection("bookings").getOne(params.bookingId)

          let paymentMethods = Array.from(
            new Set([...(params?.payMethods || []), params.paymentType]),
          )

          if(paymentMethods.length > 2){
            paymentMethods.splice(2)
          }

          const state = due === 0 ? "Confirmed" : bookingCheck?.state;

          await pb.collection("bookings").update(params?.bookingId, {
            due: due,
            paid: params.amount + bookingCheck?.paid,
            payMethod: paymentMethods,
            state: state
          });
        }

        return response;
      };

      const browserInfo = await getBrowserInfo();

      const data = {
        docket: docketId.value,
        source: params?.source,
        paymentType: params.paymentType,
        amount: parseFloat(params?.amount).toFixed(2),
        status: params.status,
        createdBy: params?.userId,
        ...(params.paymentType === "Coupon" && {
          coupon: params.identifier,
          couponQty: params.qty,
        }),
        ...(params.paymentType === "Gift Voucher" && {
          giftVoucher: params.identifier,
        }),
        browser: browserInfo.browser,
        OS: browserInfo.os,
        ipAddress: browserInfo.ip,
      };

      // Snapshot the balance so we can poll for the AfterCreateSuccess hook's
      // deduction to land before callers refetch the coupon/gift voucher list.
      const balanceCollection = params.paymentType === "Coupon" ? "coupons" : "giftVouchers";
      const balanceField = params.paymentType === "Coupon" ? "qtyRemaining" : "balanceRemaining";
      let balanceBefore: number | undefined;
      try {
        const before = await pb.collection(balanceCollection).getOne(params.identifier, {
          fields: `${balanceField},id`,
        });
        balanceBefore = Number(before[balanceField]);
      } catch (e) {
        console.error(`Error reading ${balanceCollection} balance before payment:`, e);
      }

      const result = await createPaymentRecord(data);

      if (balanceBefore !== undefined) {
        await waitForFieldChange(balanceCollection, params.identifier, balanceField, balanceBefore);
      }

      return result;
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * This function handles the payment process for applying multiple coupons or gift vouchers.
   *
   * @param params - An object containing the necessary parameters for the payment process.
   * @param params.status - The status of the payment.
   * @param params.paymentType - The type of payment (either "Coupon" or "Gift Voucher").
   * @param params.amount - The total amount to be paid.
   * @param params.identifier - An array of objects representing the coupons or gift vouchers.
   * @param params.qty - The quantity of coupons or gift vouchers.
   * @param params.toPay - The amount to be paid.
   * @param params.type - The type of payment (either "Booking" or "Membership").
   * @param params.bookingId - The ID of the booking (if the payment type is "Booking").
   * @param params.totalPaid - The total amount paid for the booking.
   * @param params.userId - The ID of the user making the payment.
   * @param params.payMethods - An array of payment methods used for the booking.
   *
   * @returns A Promise that resolves to `true` if the payment process is successful, otherwise it rejects with an error.
   */
  const payWithAutoApplyCoupons = async (params: {
    status?: string;
    paymentType: "Coupon" | "Gift Voucher";
    amount: number;
    identifier: any[];
    qty: number;
    toPay: number;
    type?: string;
    bookingId?: string;
    totalPaid?: number;
    userId: string;
    payMethods?: string[];
    source?: string;
  }) => {
    try {
      const createPaymentRecord = async (data: any) => {
        await pb.collection("dockets").update(docketId.value, {
          user: data?.owner,
          updatedBy: params?.userId,
        });

        const record = await pb.collection("docketPayments").create(data);
        docketPaymentId.value = record.id;
        const balanceRemaining = params?.toPay - data?.amount;
        const due = balanceRemaining <= 0 ? 0 : Number(balanceRemaining).toFixed(2);

        if (params?.type === "Booking" && params?.bookingId) {
          // Update due of booking record

          let bookingCheck = await pb.collection("bookings").getOne(params.bookingId)

          await pb.collection("bookings").update(params?.bookingId, {
            due: due,
            paid: (params.totalPaid || 0) + data?.amount + (bookingCheck?.paid || 0),
            payMethod: Array.from(
              new Set([...(params?.payMethods || []), params.paymentType]),
            ),
          });
        }
      };

      const browserInfo = await getBrowserInfo();

      for (const coupon of params.identifier) {
        const data = {
          docket: docketId.value,
          paymentType: params.paymentType,
          amount: coupon.amount,
          status: params.status,
          createdBy: params?.userId,
          coupon: coupon.coupon,
          couponQty: coupon.qty,
          source: params?.source,
          webResponseLoc: window.location.origin,
          browser: browserInfo.browser,
          OS: browserInfo.os,
          ipAddress: browserInfo.ip,
        };

        // Create a payment record for each coupon
        await createPaymentRecord(data);
      }

      return true;
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Handles payment for a booking using user credit or waiving the fee.
   *
   * @param bookingDetails - Details of the booking.
   * @param bookingId - ID of the booking.
   * @param paymentType - Type of payment (either 'UserCredit' or 'WaiveFee').
   *
   * @throws Throws an error if the user credit is insufficient for the booking.
   *
   * @returns Nothing.
   */
  const handleUserCreditAndWaiveFeePayment = async (
    bookingDetails: any,
    bookingId: string,
    paymentType: string,
    notes?: string,
    source = "",
  ) => {

    const browserInfo = await getBrowserInfo();

    // Create a booking record with the specified payment type
    const docketPaymentData = {
      docket: docketId.value,
      source,
      paymentType: paymentType,
      status: "Approved",
      amount:
        paymentType === "User Account" || paymentType === "User Loyalty"
          ? bookingDetails?.creditAmt
          : bookingDetails?.due,
      ...((paymentType === "User Account" || paymentType === "User Loyalty") && {
        user: bookingDetails?.owner,
      }),
      createdBy: bookingDetails?.loginUser,
      ...(paymentType === "Fee Waived" && {
        notes,
      }),
      browser: browserInfo.browser,
      OS: browserInfo.os,
      ipAddress: browserInfo.ip,
    };

    // Create a docket payment record
    const docketPaymentResponse = await pb.collection("docketPayments").create(docketPaymentData);

    if (docketPaymentResponse) {
      let paymentMethods = Array.from(
        new Set([
          ...(bookingDetails?.payMethods || []),
          paymentType === "User Account" || paymentType === "User Loyalty" ? "Account Credit" : "Fee Waived",
        ]))

      if(paymentMethods.length > 2){
        paymentMethods.splice(2)
      }
      // Update the booking due, paid, and state
      const due = paymentType === "User Account" || paymentType === "User Loyalty" ? bookingDetails?.due - bookingDetails?.creditAmt : 0
      const toPay = paymentType === "User Account" || paymentType === "User Loyalty" ? bookingDetails?.creditAmt : bookingDetails?.due;
      await pb.collection("bookings").update(bookingId, {
        state: "Confirmed",
        due: due,
        paid: bookingDetails?.paid + parseFloat(toPay),
        payMethod: paymentMethods
      });
    }
  };

  /**
   * Creates or updates a docket and docket line for a customized purchase.
   *
   * @param {Object} payment - The payment object containing details such as location, message, amount, loggedInUserId, and other relevant information.
   * @param {string} type - The type of purchase, which can be either 'transferCredit', 'giftVoucher', 'coupon', or 'addCredit'.
   *
   * @returns {Promise<void>} - A promise that resolves when the docket and docket line have been created or updated.
   */
  const createEditDocketForCustomizePurchase = async (
    payment: any,
    type: string,
  ) => {
    try {
      const transactionType = {
        transferCredit: "UserCredit",
        giftVoucher: "Gift Voucher",
        coupon: "Coupon",
        addCredit: "UserCredit",
        membership: "Membership Fee",
      }[type];

      // Check if docketId exists, if not, create or update docket
      if (!docketId.value) {
        const docketData = await pb.collection("dockets").create({
          location: payment?.location,
          description: payment?.message,
          status: "Open",
          due: payment?.amount,
          createdBy: payment?.loggedInUserId,
          user: payment?.owner,
          source: payment?.source,
        });

        docketId.value = docketData.id;
      } else {
        await pb.collection("dockets").update(docketId.value, {
          user: payment?.owner,
          description: payment?.message,
        });
      }

      // Check if docketLineId exists, if not, create or update docket line
      if (!docketLineId.value) {
        const docketLineData = await pb.collection("docketLines").create({
          docket: docketId.value,
          transactionType,
          qty: 1,
          itemPrice: payment?.amount,
          lineTotal: payment?.amount,
          status: "Available",
          createdBy: payment?.loggedInUserId,
          ...(["addCredit", "transferCredit"].includes(type) && {
            userAccountCredit: payment?.toUserId,
          }),
        });

        docketLineId.value = docketLineData.id;

      } else {
        await pb.collection("docketLines").update(docketLineId.value, {
          qty: 1,
          itemPrice: payment?.amount,
          lineTotal: payment?.amount,
        });
      }
    } catch (e) {
      console.error(e)
    }
  };

  /**
   * Creates a new docket payment in the database.
   *
   * @param {Object} payment - The payment object containing details such as amount, loggedInUserId, and other relevant information.
   * @param {string} paymentType - The type of payment, which can be either 'User Account' or 'Gift Voucher'.
   *
   * @returns {Promise<any>} - A promise that resolves with the newly created docket payment object.
   */
  const createDocketPayment = async (
    payment: any,
    paymentType: string,
    source = "",
  ) => {
    const browserInfo = await getBrowserInfo();

    const paymentData: any = {
      docket: docketId.value,
      paymentType,
      status: "Approved",
      amount: Number(payment.amount),
      createdBy: payment?.loggedInUserId,
      source,
      browser: browserInfo.browser,
      OS: browserInfo.os,
      ipAddress: browserInfo.ip,
    };

    // Conditionally add fields based on paymentType
    if (paymentType === "User Account") {
      paymentData.user = payment?.owner;
    } else if (paymentType === "Gift Voucher") {
      paymentData.giftVoucher = payment?.identifier;
    }

    return await pb.collection("docketPayments").create(paymentData);
  };

  /**
   * Creates a new coupon in the database.
   *
   * @param {Object} payment - The payment object containing details such as coupon quantity, expiry interval, and user details.
   * @param {number} payment.couponQty - The quantity of coupons to be created.
   * @param {string} payment.expiryInterval - The interval at which the coupons will expire (e.g., "Days", "Weeks", "Months").
   * @param {string} payment.owner - The ID of the user who will own the coupons.
   *
   * @returns {Promise<string>} - A promise that resolves with the ID of the newly created coupon.
   */
  const createCoupon = async (payment: any) => {
    let expiryNum = payment?.expiryNum && payment?.expiryNum > 0 ? payment.expiryNum : 1
    let expiryTime = "";
    if (payment?.expiryInterval === "Days") {
      expiryTime = moment().add(expiryNum, "days").format("YYYY-MM-DD 23:59:59");
    } else if (payment?.expiryInterval === "Weeks") {
      expiryTime = moment().add(expiryNum, "weeks").format("YYYY-MM-DD 23:59:59");
    } else if (payment?.expiryInterval === "Months") {
      expiryTime = moment().add(expiryNum, "months").format("YYYY-MM-DD 23:59:59");
    }

    const couponData = await pb.collection("coupons").create({
      name: "Coupon Purchase",
      qty: payment?.couponQty,
      activity: payment?.activity,
      qtyRemaining: payment?.couponQty,
      expiryTime: expiryTime,
      expired: false,
      user: payment?.owner,
      afterHoursOnly: payment?.afterHoursOnly ?? false,
    });

    return couponData?.id;
  };

  const updateDocketLine = async (loggedInUserId: string, updateData: any) => {
    await pb.collection("docketLines").update(docketLineId.value, {
      updatedBy: loggedInUserId,
      ...updateData,
    });
  };

  /**
   * Processes payments made by user credit.
   *
   * @param {Object} payment - The payment object containing details such as amount, owner, loggedInUserId, and other relevant information.
   * @param {string} type - The type of payment, which can be either 'giftVoucher' or 'coupon'.
   *
   * @throws {Error} - If an error occurs during the payment processing, it will be thrown.
   */
  const payByUserCredit = async (
    payment: any,
    type: string,
    source = "",
    membership: any = {},
    paymentType: string
  ) => {
    const record = await pb.collection("users").getOne(payment?.owner, { expand: "bankCards_via_user" });
    const balance = paymentType === "account" ? record?.tempBalance : record?.loyaltyBalance;

    const { currentLocationDetails } = storeToRefs(useUserStore());
    const { globalSetup } = storeToRefs(useGlobalSetupStore());
    const canGoNegative =
      paymentType === "account" &&
      (
        (!!currentLocationDetails.value?.allowUserNegAccountBalance && !!record?.expand?.bankCards_via_user?.length) ||
        !!record?.dontDailySettleAccount ||
        !!globalSetup.value?.dontDailySettleAccounts
      );

    if (record && (canGoNegative || balance >= payment?.amount)) {
      let createdId;

      if (type === "giftVoucher") {
        const giftVoucherData = await pb.collection("giftVouchers").create({
          originalAmount: payment?.amount,
          balanceRemaining: payment?.amount,
          createdBy: payment?.loggedInUserId,
          to: payment?.to,
          from: payment?.from,
          message: payment?.message,
          paid: false,
          docketLine: docketLineId.value,
        });

        createdId = giftVoucherData?.id;
      } else if (type === "coupon") {
        createdId = await createCoupon(payment);
      } else if (type === "membership") {
        const memData = {
          renewMembership: membership?.id,
          membershipRenewDate: new Date().toISOString()
        };

        await pb.collection("users").update(payment?.owner, memData);
      }

      const docketPayment = await createDocketPayment(
        payment,
        paymentType === 'account' ? "User Account" : "User Loyalty",
        source,
      );

      if (docketPayment) {
        const docketLineUpdateData = {
          ...(type === "giftVoucher" && { giftVoucher: createdId }),
          ...(type === "coupon" && {
            coupon: createdId,
            couponQty: payment?.couponQty,
            couponType: payment?.couponType,
          }),
          ...(type === "addCredit" && {
            userAccountCredit: payment?.toUserId,
          }),
          updatedBy: payment?.loggedInUserId,
        };

        await updateDocketLine(payment?.loggedInUserId, docketLineUpdateData);

        if (type === "giftVoucher") {
          await pb.collection("giftVouchers").update(createdId, {
            paid: true,
            requiresEmail: true,
            email: payment.emailAddress,
          });
        }
      }
    } else {
      lowBalanceError.value = true;
      console.log("Your available balance is not sufficient");
      throw new Error("Your available balance is not sufficient");
    }
  };

  /**
   * Processes payments made through gift vouchers.
   *
   * @param {Object} payment - The payment object containing details such as amount, identifier, loggedInUserId, and other relevant information.
   * @param {string} type - The type of payment, which can be either 'giftVoucher' or 'coupon'.
   *
   * @throws {Error} - If an error occurs during the payment processing, it will be thrown.
   */
  const payByGiftVoucher = async (
    payment: any,
    type: string,
    source = "",
    membership: any = {},
    appliedGiftVoucherDetails: any = {},
  ) => {
    try {
      const docketPayment = await createDocketPayment(
        payment,
        "Gift Voucher",
        source,
      );

      if (docketPayment?.status === "Approved") {
        if (type === "coupon") {
          const createdId = await createCoupon(payment);

          const docketLineUpdateData = {
            coupon: createdId,
            couponQty: payment?.couponQty,
            couponType: payment?.couponType,
          };

          await updateDocketLine(payment?.loggedInUserId, docketLineUpdateData);
        } else if (type === "membership") {
          const memData = {
            renewMembership: membership?.id,
            membershipRenewDate: new Date().toISOString()
          };

          await pb.collection("users").update(payment?.owner, memData);
        } else {
          await updateDocketLine(payment?.loggedInUserId, {
            userAccountCredit: payment?.toUserId,
          });
        }
      }
    } catch (e) {
      console.error("error paying by gift voucher :", e);
    }
  };

  /**
   * Processes payments made through Point of Sale (POS) for gift vouchers or coupons.
   *
   * @param {Object} payment - The payment object containing details such as amount, type, and user details.
   * @param {string} type - The type of payment, which can be either 'giftVoucher' or 'coupon'.
   *
   * @throws {Error} - If an error occurs during the payment processing, it will be thrown.
   */
  const payByPos = async (payment: any, type: string) => {
    const docketLineUpdateData = {
      ...(type === "coupon" && {
        couponQty: payment?.couponQty,
        couponType: payment?.couponType,
      }),
      ...(["transferCredit", "addCredit"].includes(type) && {
        userAccountCredit: payment?.toUserId,
      }),
      updatedBy: payment?.loggedInUserId,
    };

    await updateDocketLine(payment?.loggedInUserId, docketLineUpdateData);
  };

  /**
   * Processes a booking by creating or updating a docket line.
   *
   * @param booking - The booking object containing details such as activity, duration, and value.
   * @param docketId - The ID of the existing docket.
   * @param loggedInUser - The ID of the logged-in user.
   * @param activityMap - A map containing activity details.
   *
   * @returns {Promise<{ newDocketLine: any }>} - A promise that resolves with an object containing the new docket line.
   *
   * @throws {Error} - If an error occurs during the process, it will be thrown.
   */
  const processBooking = async (
    booking: any,
    docketId: string,
    loggedInUser: string,
    activityMap: any,
  ) => {
    try {
      const sessionPrice = activityMap[booking?.activity]?.sessionFullPrice;
      const qty = booking?.duration / activityMap[booking?.activity]?.bookingSpacing;
      const itemPrice = qty * sessionPrice;

      // Calculate the total due amount after payment
      const basePrice = booking.todAdjustment > 0 ? itemPrice + (itemPrice * booking.todAdjustment / 100) : itemPrice;
      const lineTotal = basePrice - booking.lineDiscountTotal;
      const due = lineTotal - (booking?.paid || 0);

      let newDocketLine = typeof booking?.docketLine === "object" ? booking?.docketLine?.id : booking?.docketLine;

      // A docketLine carried over from before an edit may sit on a docket that's
      // now Paid/Closed. Those can never be touched, so treat it as if there
      // were no line at all and fall through to creating a fresh one below.
      if (newDocketLine) {
        const existingLine = await pb.collection("docketLines").getOne(newDocketLine, {
          expand: "docket",
          fields: "id,expand.docket.status",
          requestKey: null,
        });
        if (existingLine?.expand?.docket?.status === "Paid" || existingLine?.expand?.docket?.status === "Closed") {
          newDocketLine = null;
        }
      }

      if (!newDocketLine) {
        // Step 2: Create a new docketline under the existing docket
        newDocketLine = await pb.collection("docketLines").create({
          docket: docketId,
          transactionType: "Event",
          qty,
          docketDiscount: booking.docketDiscount,
          todAdjustment: booking.todAdjustment,
          discountApplied: booking.discountApplied,
          lineDiscountTotal: booking.lineDiscountTotal,
          itemPrice: sessionPrice,
          lineTotal: lineTotal,
          status: "Available",
          booking: booking?.id,
          createdBy: loggedInUser,
        });

        // Step 3: Update the booking with the new docketLine
        await pb.collection("bookings").update(booking.id, {
          due: due,
          docketLine: newDocketLine.id,
          updatedBy: loggedInUser,
        });
      } else {
        // Existing line is still on an open docket — keep it in sync with the recalculated amount
        await pb.collection("docketLines").update(newDocketLine, {
          qty,
          docketDiscount: booking.docketDiscount,
          todAdjustment: booking.todAdjustment,
          discountApplied: booking.discountApplied,
          lineDiscountTotal: booking.lineDiscountTotal,
          itemPrice: sessionPrice,
          lineTotal: lineTotal,
        });
        await pb.collection("bookings").update(booking.id, {
          due: due,
          updatedBy: loggedInUser,
        });
      }
      return { newDocketLine };
    } catch (error) {
      console.error("Error creating or updating docket line:", error);
      throw error;
    }
  };

  /**
   * Processes event payments by Point of Sale (POS).
   *
   * @param params - An object containing the following properties:
   *   - eventId: The ID of the event.
   *   - loggedInUser: The ID of the logged-in user.
   *   - activityMap: A map containing activity details.
   *   - due: The total amount due for the event.
   *
   * @returns {Promise<void>} - A promise that resolves when the event payment processing is complete.
   */
  const eventDuePayByPos = async (params: any) => {
    try {
      const { eventId, loggedInUser, activityMap, due, activityDiscounts } = params;
      const eventData = await pb.collection("events").getOne(eventId, {
        expand: "bookings",
        requestKey: null,
      });

      // If docketId isn't already set, find it from any booking that already has a docket line —
      // but never adopt a docket that's Paid/Closed, those can't accept new/changed lines.
      if (!docketId.value) {
        const bookingsWithDocketLine = (eventData?.expand?.bookings || []).filter((b: any) => b?.docketLine);
        if (bookingsWithDocketLine.length > 0) {
          const rawDocketLine = bookingsWithDocketLine[0].docketLine;
          const targetLineId = typeof rawDocketLine === "object" ? rawDocketLine?.id : rawDocketLine;
          if (targetLineId) {
            const existingLine = await pb.collection("docketLines").getOne(targetLineId, { expand: 'docket', fields: 'docket,expand.docket.status', requestKey: null });
            const existingDocketStatus = existingLine?.expand?.docket?.status;
            if (existingDocketStatus !== "Paid" && existingDocketStatus !== "Closed") {
              docketId.value = existingLine?.docket || '';
            }
          }
        }
      }

      // Check if any bookings have a docket; if not, create a new one
      for (const booking of eventData?.expand?.bookings || []) {
        if (booking?.due !== 0) {
          // If no docket has been created yet, create one
          const data = {
            location: params?.location,
            source: "Booking",
            createdBy: loggedInUser,
          }
          try {
            if (!docketId.value) {
              const newDocket = await pb.collection("dockets").create(data);
              docketId.value = newDocket.id;
            }
          } catch (error) {
            console.error("Error creating docket:", error);
            throw error;
          }

          // Get activity-specific discount data for this booking's activity
          const activityDiscount = activityDiscounts?.[booking.activity] || {
            todAdjustment: 0,
            docketDiscount: 0,
            discountApplied: "None",
            lineDiscountTotal: 0
          };

          booking.todAdjustment = activityDiscount.todAdjustment;
          booking.docketDiscount = activityDiscount.docketDiscount;
          booking.discountApplied = activityDiscount.discountApplied;
          booking.lineDiscountTotal = activityDiscount.lineDiscountTotal;
         
          await processBooking(
            booking,
            docketId.value,
            loggedInUser,
            activityMap,
          );
        }
      }

      if (!docketId.value) {
        throw new Error("No payable event bookings were found.");
      }

      return docketId.value;
    } catch (e) {
      console.error("Error processing event payment by POS:", e);
      throw e;
    }
  };

  /**
   * Processes event payments by waiving the fee.
   *
   * @param params - An object containing the following properties:
   *   - eventId: The ID of the event.
   *   - loggedInUser: The ID of the logged-in user.
   *   - activityMap: A map containing activity details.
   *   - due: The total amount due for the event.
   *
   * @returns {Promise<void>} - A promise that resolves when the event payment processing is complete.
   */
  const eventDuePayByWaiveFee = async (params: any) => {
    try {
      const { eventId, loggedInUser, activityMap, due, activityDiscounts, notes } = params;
      const eventData = await pb.collection("events").getOne(eventId, {
        expand: "bookings",
      });

      let bookingDues = 0;

      // Iterate over all bookings in the event
      for (const booking of eventData?.expand?.bookings || []) {
        if (booking?.due !== 0) {
          bookingDues += booking?.due;

          // Create a docket if one doesn't exist for this event
          if (!docketId.value) {
            const newDocket = await pb.collection("dockets").create({
              location: params?.location,
              source: "Booking",
              createdBy: loggedInUser,
            });
            docketId.value = newDocket.id;
          }

          // Get activity-specific discount data for this booking's activity
          const activityDiscount = activityDiscounts?.[booking.activity] || {
            todAdjustment: 0,
            docketDiscount: 0,
            discountApplied: "None",
            lineDiscountTotal: 0
          };

          // Process the booking and add docketLines under the same docket
          booking.todAdjustment = activityDiscount.todAdjustment;
          booking.docketDiscount = activityDiscount.docketDiscount;
          booking.discountApplied = activityDiscount.discountApplied;
          booking.lineDiscountTotal = activityDiscount.lineDiscountTotal;
          await processBooking(
            booking,
            docketId.value,
            loggedInUser,
            activityMap,
          );

          let bookingCheck = await pb.collection("bookings").getOne(booking.id)

          // Update the booking with payment details (but not the docket yet)
          await pb.collection("bookings").update(booking.id, {
            due: 0,
            paid: booking?.due + (bookingCheck?.paid || 0),
            paymentType: "Fee Waived",
            updatedBy: loggedInUser,
          });
        }
      }

      // Step 4: create docketPayment record
      if (bookingDues) {
        const browserInfo = await getBrowserInfo();

        await pb.collection("docketPayments").create({
          docket: docketId.value,
          paymentType: "Fee Waived",
          status: "Approved",
          amount: bookingDues,
          notes,
          createdBy: loggedInUser,
          browser: browserInfo.browser,
          OS: browserInfo.os,
          ipAddress: browserInfo.ip,
        });
      }

    } catch (e) {
      console.error("Error processing event payment by fee waiver:", e);
    }
  };

  const initiateDocketForEvent = async (eventDetails: any) => {
    const { eventId, loggedInUser, activityMap, due, activityDiscounts } = eventDetails;
    const eventData = await pb.collection("events").getOne(eventId, {
      expand: "bookings",
    });

    let bookingValue = 0;

    // Iterate over all bookings in the event
    for (const booking of eventData?.expand?.bookings || []) {
      if (booking?.due !== 0) {
        bookingValue += booking.value;

        // Create a docket if one doesn't exist for this event
        if (!docketId.value) {
          const discountSources = eventDetails?.discountEvents
            ? ["DiscountCode"]
            : [];

          const newDocket = await pb.collection("dockets").create({
            location: eventDetails?.location,
            discountProduct: eventDetails?.discountProduct,
            discountBookings: eventDetails?.discountBookings,
            discountEvents: eventDetails?.discountEvents,
            user: eventDetails?.owner,
            source: "Booking",
            discountSources,
            createdBy: loggedInUser,
          });
          docketId.value = newDocket.id;
        }

        // Get activity-specific discount data for this booking's activity
        const activityDiscount = activityDiscounts?.[booking.activity] || {
          todAdjustment: 0,
          docketDiscount: 0,
          discountApplied: "None",
          lineDiscountTotal: 0
        };

        // Process the booking and add docketLines under the same docket
        booking.todAdjustment = activityDiscount.todAdjustment;
        booking.docketDiscount = activityDiscount.docketDiscount;
        booking.discountApplied = activityDiscount.discountApplied;
        booking.lineDiscountTotal = activityDiscount.lineDiscountTotal;
        await processBooking(
          { ...booking },
          docketId.value,
          loggedInUser,
          activityMap,
        );
      }
    }
  };

  return {
    couponList,
    docketId,
    docketLineId,
    docketPaymentId,
    lowBalanceError,
    isPendingBooking,
    processingPaymentsOverlay,
    getCouponList,
    payWithGiftVoucherOrCoupon,
    initiateDocketForBooking,
    handleUserCreditAndWaiveFeePayment,
    payWithAutoApplyCoupons,
    createEditDocketForCustomizePurchase,
    payByUserCredit,
    payByGiftVoucher,
    payByPos,
    eventDuePayByPos,
    eventDuePayByWaiveFee,
    initiateDocketForEvent,
    processBooking,
  };
});
